/* eslint react-hooks/exhaustive-deps: "warn" */

import { useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { useIntersection } from 'hooks/useIntersection';

import styles from './WaterfallEnrichment.module.scss';

import type { WaterfallEnrichmentProps } from './WaterfallEnrichment.types';

type FieldKey = 'email' | 'mobile' | 'skills' | 'education' | 'revenue';

// Display order of the field chips (left-to-right), independent of the order
// they are actually resolved in.
const FIELDS: { key: FieldKey; label: string }[] = [
  { key: 'email', label: 'Work email' },
  { key: 'mobile', label: 'Mobile' },
  { key: 'skills', label: 'Skills' },
  { key: 'education', label: 'Education' },
  { key: 'revenue', label: 'Company revenue' },
];

// Which partner (1-based) resolves each field — the "waterfall" hits.
const FIELD_HIT: Record<FieldKey, number> = {
  email: 3,
  mobile: 5,
  education: 7,
  skills: 9,
  revenue: 11,
};

const NAMES: Record<FieldKey, string> = {
  email: 'work email',
  mobile: 'mobile',
  education: 'education',
  skills: 'skills',
  revenue: 'company revenue',
};

const TOTAL = 15; // partners connected
const LAST = 11; // partner that resolves the final field
const STEP = 950; // ms between partner checks
const RESTART = 3600; // ms to hold the completed state before replaying

const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);

const hitAt = (frame: number): FieldKey | undefined =>
  (Object.keys(FIELD_HIT) as FieldKey[]).find((key) => FIELD_HIT[key] === frame);

export const WaterfallEnrichment = ({ label = 'Waterfall enrichment', lede, className }: WaterfallEnrichmentProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useIntersection(ref, { continuous: true, threshold: 0.25 });

  const [reduced, setReduced] = useState(false);
  // frame 0 = "starting the run"; 1..LAST = partner being checked.
  const [frame, setFrame] = useState(0);

  useEffect(() => {
    const query = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (query?.matches) {
      setReduced(true);
      setFrame(LAST);
    }
  }, []);

  useEffect(() => {
    if (reduced || !inView) {
      return;
    }

    let i = 0;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;

    setFrame(0);

    const advance = () => {
      if (cancelled) {
        return;
      }

      i += 1;
      setFrame(i);

      if (i >= LAST) {
        timer = setTimeout(() => {
          if (cancelled) {
            return;
          }
          i = 0;
          setFrame(0);
          timer = setTimeout(advance, STEP);
        }, RESTART);
      } else {
        timer = setTimeout(advance, STEP);
      }
    };

    timer = setTimeout(advance, STEP);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [inView, reduced]);

  const displayIndex = frame < 1 ? 1 : frame;
  const foundCount = FIELDS.filter((f) => FIELD_HIT[f.key] <= frame).length;
  const done = frame >= LAST;
  const justFound = frame >= 1 ? hitAt(frame) : undefined;
  const barPct = Math.round((foundCount / FIELDS.length) * 100);

  const routeTag = done ? 'Complete' : justFound ? 'Match' : 'Continue';

  let message: string;
  if (frame < 1) {
    message = 'Starting the run';
  } else if (done) {
    message = `All five fields filled after ${LAST} sources. The remaining ${TOTAL - LAST} were never queried.`;
  } else if (justFound) {
    message = `Returned ${NAMES[justFound]}. Continuing for the remaining fields.`;
  } else {
    message = 'No new field. Checking the next partner…';
  }

  return (
    <div ref={ref} className={cn(styles.card, className)}>
      <div className={styles.bar}>
        <span className={styles.icon} aria-hidden="true" />
        <strong className={styles.title}>{label}</strong>
      </div>

      {lede && <p className={styles.lede}>{lede}</p>}

      <div className={styles.status}>
        <span className={styles.statusText} aria-live="polite">
          Checking partner <b>{pad(displayIndex)}</b> of {TOTAL}
        </span>
        <span key={foundCount} className={cn(styles.pill, foundCount > 0 && styles.pillPop)}>
          <b>{foundCount}</b> {foundCount === 1 ? 'field' : 'fields'} found
        </span>
      </div>

      <div className={styles.track}>
        <span className={styles.trackFill} style={{ width: `${barPct}%` }} />
      </div>

      <div className={styles.route}>
        <span className={cn(styles.routeNum, justFound && styles.routeNumHit)}>{displayIndex}</span>
        <div className={styles.routeBody}>
          <span className={styles.routeLabel}>Partner route</span>
          <strong className={styles.routeTitle}>Partner {pad(displayIndex)}</strong>
          <p className={styles.routeMsg}>{message}</p>
        </div>
        <span className={styles.routeChip}>{routeTag}</span>
      </div>

      <div className={styles.fields}>
        <span className={styles.fieldsKey}>
          <i className={styles.ring} aria-hidden="true" />
          Fields
        </span>
        <div className={styles.chips}>
          {FIELDS.map((field) => {
            const found = FIELD_HIT[field.key] <= frame;
            const landing = justFound === field.key;

            return (
              <span
                key={field.key}
                className={cn(styles.chip, found && styles.chipFound, landing && styles.chipLanding)}
              >
                {field.label}
                <i className={styles.chipTag}>{found ? `P${pad(FIELD_HIT[field.key])}` : ''}</i>
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
};
